<template>
    <div
        class="lds-widget-banner-container"
        :style="widgetColors"
    >
        <a
            :href="url"
            target="_blank"
            class="lds-widget-banner"
        >
            <div class="lds-widget-banner__info">
                <p class="lds-widget-banner__title">
                    {{ widgetTitle }}
                </p>
                <p class="lds-widget-banner__text">
                    {{ widgetSubtitle }}
                </p>
                <div class="lds-widget-banner__btn">
                    {{ textButton }}
                </div>
                <marking
                    :url="url"
                    :adv-name="advName"
                    :adv-inn="advInn"
                    :is-offer-target="isOfferTarget"
                />
            </div>
            <div class="lds-widget-banner__img">
                <picture>
                    <source
                        type="image/webp"
                        :srcset="currentImage"
                    >
                    <img
                        width="350"
                        height="250"
                        :src="`${s3Domain}images/banner-1.png`"
                        alt="banner"
                    >
                </picture>
            </div>
            <icon-advertising />
        </a>
    </div>
</template>

<script setup>
import {computed, ref, toRef} from 'vue';
import useThemes from './composables/useThemes.js';
import getS3Domain from '../../common/s3Domain';
import IconAdvertising from '../../common/icons/IconAdvertising.vue';
import Marking from '../../common/components/Marking.vue';

const props = defineProps({
    settings: {
        type: Object,
        default: () => {
        },
    },

    url: {
        /** Может быть не задан для превью */
        type: [String, null],
        required: true,
    },

    widgetTitle: {
        type: String,
        default: '',
    },

    widgetSubtitle: {
        type: String,
        default: '',
    },

    textButton: {
        type: String,
        default: 'Получить деньги',
    },

    advInn: {
        type: String,
        default: '',
    },

    advName: {
        type: String,
        default: '',
    },

    isOfferTarget: {
        type: Boolean,
        default: true,
    },
});

const settings = toRef(props, 'settings');
const s3Domain = ref(getS3Domain());

const { widgetColors } = useThemes({ settings });

const currentImage = computed(() => (props.settings.imageBannerMini ? `${s3Domain.value}${props.settings.imageBannerMini}` : `${s3Domain.value}images/banner-1.webp`));

</script>

<style lang="scss" scoped>
.lds-widget-banner-container {
    container-name: lds-banner;
    container-type: inline-size;
}

.lds-widget-banner {
    width: 100%;
    display: flex;
    justify-content: space-between;
    box-sizing: border-box;

    border-radius: 24px !important;
    background-color: var(--lw-widget-bg);

    font-weight: 500 !important;
    text-decoration: none !important;

    text-align: left;
    cursor: pointer;

    position: relative;

    * {
        box-sizing: border-box !important;
        font-family: var(--lw-font-family), 'Arial', sans-serif !important;
        outline: none !important;
    }

    &__img {
        position: relative;
        flex-shrink: 0;
        overflow: hidden;

        border-radius: 15px !important;

        img {
            max-width: 100%;
            max-height: 100%;
            vertical-align: middle;
            object-fit: cover;
        }
    }

    &__info {
        max-width: 808px;
        padding: 24px 40px;

        display: flex;
        flex-direction: column;
        justify-content: space-between;
    }

    &__title {
        margin: 0 !important;

        font-size: 32px !important;
        font-weight: 600 !important;
        line-height: 1.21;
        color: var(--lw-title);
    }

    &__text {
        margin: 8px 0 0 !important;

        font-size: 16px !important;
        line-height: 1.21;
        font-weight: 500 !important;
        white-space: pre-line;
        color: var(--lw-text);
    }

    &__btn {
        display: block;
        align-items: center;
        justify-content: center;

        width: fit-content;

        padding: 10px 16px !important;
        margin-top: 16px !important;

        font-size: 16px !important;
        font-weight: 500 !important;
        line-height: 1.5;
        text-decoration: none !important;
        color: var(--lw-btn-color);

        background-color: var(--lw-btn-bg);
        border: 0 !important;
        border-radius: 8px !important;
        outline: 0 !important;
        box-shadow: 0 0 20px 0 var(--lw-btn-shadow);

        cursor: pointer;

        transition: .2s;

        &:hover {
            background-color: var(--lw-btn-hover-bg);
            color: var(--lw-btn-hover-color);
        }

        &:active {
            background-color: var(--lw-btn-active-bg);
            color: var(--lw-btn-active-color);
        }
    }

    .lds-widget-marking {
        margin-top: 16px !important;
    }

    .widget-advertising-icon {
        position: absolute;
        top: 16px;
        right: 16px;
    }

    @container (width < 960px) {
        &__img {
            width: 253px;
            height: 218px;

            img {
                max-width: 100%;
            }
        }

        &__info {
            padding: 24px 32px;
        }

            &__title {
            font-size: 24px !important;
            line-height: 1.2;
        }
    }

    @media (max-width: 960px) {
        &__img {
            width: 253px;
            height: 218px;

            img {
                max-width: 100%;
            }
        }

        &__info {
            padding: 24px 32px;
        }

        &__title {
            font-size: 24px !important;
            line-height: 1.2;
        }
    }

    @container (width < 700px) {
        flex-direction: column-reverse;
        align-items: flex-start;

        max-width: 320px;
        margin: 0 auto;
        padding: 16px;
        border-radius: 16px !important;

        &__img {
            width: 100%;
            height: auto;

            img {
                height: 100%;
            }
        }

        &__info {
            padding: 16px 0 0 !important;
        }

        &__title {
            font-size: 16px !important;
            line-height: 22px;
        }

        &__text {
            font-size: 14px !important;
            line-height: 20px;
        }

        &__btn {
            flex-direction: row-reverse;
            margin-top: 16px !important;
        }

        .widget-advertising-icon {
            left: 16px;
        }
    }

    @media (max-width: 700px) {
        flex-direction: column-reverse;
        align-items: flex-start;

        max-width: 320px;
        padding: 16px;
        margin: 0 auto;
        border-radius: 16px !important;

        &__img {
            width: 100%;
            height: auto;

            img {
                height: 100%;
            }
        }

        &__info {
            padding: 16px 0 0 !important;
        }

        &__title {
            font-size: 16px !important;
            line-height: 22px;
        }

        &__text {
            font-size: 14px !important;
            line-height: 20px;
        }

        &__btn {
            flex-direction: row-reverse;
            margin-top: 16px !important;
        }

        .widget-advertising-icon {
            left: 16px;
        }
    }
}
</style>
